const PERMISSION = "You don't have permission to edit this column.";
const GONE = "This column or item no longer exists. Refresh the view.";
const BUSY = "monday is busy. Try again in a moment.";
const OFFLINE = "Couldn't reach monday. Check your connection and try again.";
const FALLBACK = "Couldn't save this value. Try again.";

const PERMISSION_CODES = ["USER_UNAUTHORIZED", "UserUnauthorizedException", "Unauthorized"];
const GONE_CODES = [
  "InvalidColumnIdException",
  "InvalidItemIdException",
  "InvalidBoardIdException",
  "ResourceNotFoundException",
];
const BUSY_CODES = [
  "ComplexityException",
  "RATE_LIMIT_EXCEEDED",
  "RateLimitExceeded",
  "maxConcurrencyExceeded",
  "IP_RATE_LIMIT_EXCEEDED",
];
const VALUE_CODES = ["ColumnValueException", "InvalidArgumentException"];

/** monday appends this pointer to every generic "invalid value" error; it means nothing to a user. */
const GENERIC_VALUE_MESSAGE = /please check our API documentation/i;

const parseResponse = (error: unknown): any => {
  if (!(error instanceof Error)) return null;
  // Inside monday's iframe the SDK rejects with the GraphQL response attached as `data`.
  const attached = (error as Error & { data?: unknown }).data;
  if (attached && typeof attached === "object") return attached;
  try {
    return JSON.parse(error.message);
  } catch {
    return null;
  }
};

/**
 * A user-facing reason for a failed item view save. Failures reach the item view as a monday
 * response either serialised into the message (`handleMondayError`) or attached as `data` (the
 * SDK's own rejection).
 */
export const describeSaveError = (error: unknown): string => {
  if (error instanceof TypeError) return OFFLINE;

  const response = parseResponse(error);
  if (!response) return FALLBACK;

  const errors: any[] = Array.isArray(response.errors) ? response.errors : [];
  const codes: string[] = [
    response.error_code,
    ...errors.map((entry) => entry?.extensions?.code),
  ].filter(Boolean);
  const has = (list: string[]) => codes.some((code) => list.includes(code));

  if (has(PERMISSION_CODES)) return PERMISSION;
  if (has(GONE_CODES)) return GONE;
  if (has(BUSY_CODES) || response.status_code === 429) return BUSY;
  if (has(VALUE_CODES)) {
    const reason: string | undefined = errors.find((entry) =>
      VALUE_CODES.includes(entry?.extensions?.code),
    )?.message;
    return reason && !GENERIC_VALUE_MESSAGE.test(reason)
      ? `monday rejected this value: ${reason.replace(/\.$/, "")}.`
      : "monday rejected this value.";
  }
  return FALLBACK;
};
